import { useState } from 'react';
import type { CharacterBenefits } from '../../hooks/useCharacterBenefits';
import type { CharacterDraft } from '../../stores/characterCreationStore';
import { usePlaySessionStore, createDefaultRpLayout } from '../../stores/playSessionStore';
import { RpSectionRenderer } from './RpSectionRenderer';
import { RpCustomizePanel } from './RpCustomizePanel';

interface PlayModeRolePlayProps {
  benefits: CharacterBenefits;
  draft: CharacterDraft;
  sessionNotes: string;
  onNotesChange: (notes: string) => void;
}

export function PlayModeRolePlay({
  benefits,
  draft,
  sessionNotes,
  onNotesChange,
}: PlayModeRolePlayProps) {
  const [isCustomizing, setIsCustomizing] = useState(false);

  const rpLayout = usePlaySessionStore((s) => s.activeSession?.rpLayout) ?? createDefaultRpLayout();
  const moveRpSection = usePlaySessionStore((s) => s.moveRpSection);
  const toggleRpSectionVisibility = usePlaySessionStore((s) => s.toggleRpSectionVisibility);
  const addRpCustomSection = usePlaySessionStore((s) => s.addRpCustomSection);
  const updateRpCustomSection = usePlaySessionStore((s) => s.updateRpCustomSection);
  const removeRpCustomSection = usePlaySessionStore((s) => s.removeRpCustomSection);

  return (
    <div className="relative">
      {/* Customize button */}
      <div className="flex justify-end mb-3 max-w-2xl mx-auto">
        <button
          onClick={() => setIsCustomizing(!isCustomizing)}
          className={`p-1.5 rounded transition-colors ${
            isCustomizing
              ? 'bg-accent-bronze/20 text-accent-gold'
              : 'text-text-muted hover:text-text-secondary'
          }`}
          aria-label={isCustomizing ? 'Close customize panel' : 'Customize RP layout'}
          title="Customize layout"
        >
          <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      {/* Customize panel */}
      {isCustomizing && (
        <div className="max-w-2xl mx-auto mb-4">
          <RpCustomizePanel
            sectionOrder={rpLayout.sectionOrder}
            hiddenSections={rpLayout.hiddenSections}
            customSections={rpLayout.customSections}
            onMove={moveRpSection}
            onToggleVisibility={toggleRpSectionVisibility}
            onAddCustomSection={addRpCustomSection}
            onRemoveCustomSection={removeRpCustomSection}
            onRenameCustomSection={(id, title) => updateRpCustomSection(id, { title })}
            onClose={() => setIsCustomizing(false)}
          />
        </div>
      )}

      {/* Section renderer */}
      <RpSectionRenderer
        sectionOrder={rpLayout.sectionOrder}
        hiddenSections={rpLayout.hiddenSections}
        customSections={rpLayout.customSections}
        benefits={benefits}
        draft={draft}
        sessionNotes={sessionNotes}
        onNotesChange={onNotesChange}
        onCustomSectionContentChange={(id, content) => updateRpCustomSection(id, { content })}
      />
    </div>
  );
}
